import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import { useAuth } from './lib/auth'
import { HouseholdProvider } from './lib/household'
import { Layout, PublicLayout } from './components/Layout'
import { PageLoading, ServerUnreachable } from './components/ui'
import { ActivityPage } from './pages/Activity'
import { Home } from './pages/Home'
import { AddItemPage } from './pages/AddItem'
import { ItemDetailPage } from './pages/ItemDetail'
import { SearchPage } from './pages/Search'
import { StoragePage } from './pages/Storage'
import { LabelsPage } from './pages/Labels'
import { ResolveCodePage } from './pages/ResolveCode'
import { TrashPage } from './pages/Trash'
import { NotificationsPage } from './pages/Notifications'

const View3DPage = lazy(() => import('./pages/View3D').then((m) => ({ default: m.View3DPage })))
import { ForgotPasswordPage, LoginLinkPage, LoginPage, RegisterPage, ResetPasswordPage, VerifyEmailPage } from './pages/auth'
import { HouseholdPage } from './pages/Household'
import { JoinPage } from './pages/Join'
import { SettingsPage } from './pages/Settings'
import { OAuthAuthorizePage } from './pages/OAuthAuthorize'
import { PrivacyPage } from './pages/Privacy'
import { HelpPage } from './pages/Help'

// Administration chargée à la demande (réservée à l'administrateur, inutile pour les autres).
const AdminPage = lazy(() => import('./pages/admin/Admin').then((m) => ({ default: m.AdminPage })))

/**
 * Session encore inconnue (réponse de `/me` en attente) ou serveur injoignable : écran d'attente ou message clair,
 * dans le gabarit public. Rien pour les pages publiques (aide, confidentialité…), qui s'affichent sans attendre.
 */
function SessionGate({ children }: { children: ReactNode }) {
  const { loading, unreachable } = useAuth()
  if (loading) return <Public><PageLoading /></Public>
  if (unreachable) return <Public><ServerUnreachable /></Public>
  return <>{children}</>
}

function Private({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return (
    <SessionGate>
      <PrivateInner wide={wide}>{children}</PrivateInner>
    </SessionGate>
  )
}

function PrivateInner({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const { me } = useAuth()
  const location = useLocation()
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  return (
    <HouseholdProvider>
      <Layout wide={wide}>{children}</Layout>
    </HouseholdProvider>
  )
}

function Public({ children }: { children: ReactNode }) {
  return <PublicLayout>{children}</PublicLayout>
}

export function App() {
  const { me } = useAuth()
  return (
    <Routes>
      <Route path="/login" element={<SessionGate>{me ? <Navigate to="/" replace /> : <Public><LoginPage /></Public>}</SessionGate>} />
      <Route path="/register" element={<SessionGate>{me ? <Navigate to="/" replace /> : <Public><RegisterPage /></Public>}</SessionGate>} />
      <Route path="/login-link" element={<Public><LoginLinkPage /></Public>} />
      <Route path="/forgot-password" element={<Public><ForgotPasswordPage /></Public>} />
      <Route path="/reset-password" element={<Public><ResetPasswordPage /></Public>} />
      <Route path="/verify-email" element={<Public><VerifyEmailPage /></Public>} />
      <Route path="/join/:code" element={<Public><JoinPage /></Public>} />
      <Route path="/confidentialite" element={<Public><PrivacyPage /></Public>} />
      <Route path="/aide" element={<Public><HelpPage /></Public>} />
      <Route path="/" element={<Private><Home /></Private>} />
      <Route path="/add" element={<Private><AddItemPage /></Private>} />
      <Route path="/search" element={<Private><SearchPage /></Private>} />
      <Route path="/storage" element={<Private><StoragePage /></Private>} />
      <Route
        path="/view3d"
        element={
          <Private wide>
            <Suspense fallback={<PageLoading />}>
              <View3DPage />
            </Suspense>
          </Private>
        }
      />
      <Route path="/labels" element={<Private wide><LabelsPage /></Private>} />
      <Route path="/trash" element={<Private><TrashPage /></Private>} />
      <Route path="/r/:publicCode" element={<Private><ResolveCodePage /></Private>} />
      <Route path="/items/:itemId" element={<Private><ItemDetailPage /></Private>} />
      <Route path="/activity" element={<Private><ActivityPage /></Private>} />
      <Route path="/notifications" element={<Private><NotificationsPage /></Private>} />
      <Route path="/household" element={<Private><HouseholdPage /></Private>} />
      <Route path="/settings" element={<Private><SettingsPage /></Private>} />
      <Route path="/oauth/authorize" element={<Private><OAuthAuthorizePage /></Private>} />
      <Route
        path="/admin/*"
        element={
          <Private wide>
            <Suspense fallback={<PageLoading />}>
              <AdminPage />
            </Suspense>
          </Private>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
